import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Wallet } from "lucide-react";
import { fetchInstructorCompensation, centsToUsd, type Compensation } from "@/lib/personnel/compensation";
import { supabase } from "@/integrations/supabase/client";

export default function CompensationSection({ instructor }: { instructor: any }) {
  const [comp, setComp] = useState<Compensation | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const c = await fetchInstructorCompensation(instructor.id);
      setComp(c);
      const { data } = await (supabase.from as any)("instructor_class_assignments")
        .select("id,class_title,class_slug,status,assignment_kind")
        .eq("instructor_id", instructor.id)
        .in("status", ["active", "temporary", "upcoming"]);
      setAssignments(data ?? []);
    })();
  }, [instructor.id]);

  // Pay AMOUNT and payment TIMING are separate facts. The amount is always a
  // per-meeting rate applied to the meetings actually scheduled/taught; the
  // frequency only says when the money moves.
  const rateLabel = (() => {
    const t = String(instructor.pay_rate_type ?? ""), a = instructor.pay_rate_amount, h = instructor.hourly_rate;
    if (a && (t === "flat_per_meeting" || t === "per_meeting")) return `$${Number(a).toFixed(2)} per class`;
    if (a && t) return `$${Number(a).toFixed(2)} ${t.replace(/_/g, " ")}`;
    if (h) return `$${Number(h).toFixed(2)}/hour (legacy)`;
    return "Not set";
  })();

  const timingLabel = instructor.pay_frequency?.trim() || "Standard payroll schedule";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4" /> Compensation</CardTitle>
        <CardDescription>Derived from the payroll ledger and current assignments.</CardDescription>
      </CardHeader>
      <CardContent className="text-sm space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Pay rate" value={rateLabel} />
          <Stat label="Payment timing" value={timingLabel} />
          <Stat label="Payment method" value={instructor.payment_method ?? "—"} />
          <Stat label="Classes assigned" value={String(assignments.length)} />
        </div>
        <p className="text-xs text-muted-foreground">
          Amounts are always derived from scheduled payable meetings × the per-meeting rate.
          Payment timing never sets the amount.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t pt-3">
          <Stat label={`Year-to-date (${comp?.taxYear ?? ""})`} value={comp ? centsToUsd(comp.ytdCents) : "…"} />
          <Stat label={`Current semester (${comp?.currentSemester ?? ""})`} value={comp ? centsToUsd(comp.currentSemesterCents) : "…"} />
          <Stat label="Lifetime earnings" value={comp ? centsToUsd(comp.lifetimeCents) : "…"} />
          <Stat label="Avg monthly" value={comp ? centsToUsd(comp.avgMonthlyCents) : "…"} />
        </div>
        {assignments.length > 0 && (
          <div className="border-t pt-3">
            <div className="text-xs text-muted-foreground mb-1">Currently teaching</div>
            <ul className="list-disc pl-5">
              {assignments.map((a) => <li key={a.id}>{a.class_title ?? a.class_slug} <span className="text-xs text-muted-foreground">({a.status})</span></li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
